import React, { useRef } from 'react';
import { st, classes } from './ColumnController.st.css';
import './ColumnController.responsive.st.css';
// TODO: temp. should be numeric input
import Input from '../Input';
import Text from '../Text';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { BaseUiNextContext, } from '../../utils/BaseUINextContext';
import { HorizontalDragHandle as ClassicHandle } from '@wix/wix-ui-icons-common/classic-editor/system';
import { HorizontalDragHandle as ResponsiveHandle } from '@wix/wix-ui-icons-common/responsive-editor/system';
import { stVars } from '../../foundations/Colors.st.css';
import { mergeRefs, debounce } from './utils';
import ColumnControllerInput from './ColumnControllerInput';
const columnsProperties = {
    previousPercentages: {
        left: 0,
        right: 0,
    },
    previousX: 0,
};
const thumbsHeight = 16;
const ColumnController = React.forwardRef(({ columns, onChange, minValue = 1, allowReorder = false, addButtonNode, removeButtonNode, duplicateButtonNode, dataHook, onDragStart, onDragUpdate, onDragEnd, allowActions, disabled = false, }, ref) => {
    const context = React.useContext(BaseUiNextContext);
    const barRef = useRef(null);
    const handleRef = useRef([]);
    const [isMouseDraggingHandle, setIsMouseDraggingHandle] = React.useState(false);
    const [blockDragged, setBlockDragged] = React.useState(-1);
    const [draggedHandleIndex, setDraggedHandleIndex] = React.useState(-1);
    const [keyboardDraggingHandle, setKeyboardDraggingHandle] = React.useState(-1);
    const [selectedBlock, setSelectedBlock] = React.useState(0);
    const tabIndex = !disabled ? 0 : -1;
    // on mount it sets the droppable area width
    React.useEffect(() => {
        const removeDragHandleEvent = () => {
            setIsMouseDraggingHandle(false);
            setDraggedHandleIndex(-1);
        };
        window.addEventListener('mouseup', removeDragHandleEvent, true);
        return () => {
            window.removeEventListener('mouseup', removeDragHandleEvent, true);
        };
    }, []);
    // keeps focusing the drag handle after pressing right or left arrow key
    React.useEffect(() => {
        if (keyboardDraggingHandle > -1) {
            handleRef.current[keyboardDraggingHandle].focus();
        }
    }, [keyboardDraggingHandle, columns]);
    const delayIndexChange = debounce((leftIndex) => {
        if (draggedHandleIndex !== leftIndex) {
            return;
        }
    }, 1000);
    const handleMouseMove = ({ event, leftIndex }) => {
        if (!isMouseDraggingHandle) {
            return;
        }
        delayIndexChange(leftIndex);
        const rightDraggedIndex = draggedHandleIndex + 1;
        const currentX = event.clientX;
        const barWidth = barRef?.current.offsetWidth;
        const currentPercentages = { ...columnsProperties.previousPercentages };
        const percentageDiff = ((columnsProperties.previousX - currentX) /
            (barWidth - thumbsSpace * thumbsHeight)) *
            100;
        if (columnsProperties.previousPercentages.left - percentageDiff <
            minValue ||
            columnsProperties.previousPercentages.right + percentageDiff <
                minValue) {
            return;
        }
        else if (columnsProperties.previousPercentages.left - percentageDiff <
            0) {
            currentPercentages.right =
                columnsProperties.previousPercentages.right +
                    Math.abs(columnsProperties.previousPercentages.left);
            currentPercentages.left = 0;
        }
        else if (columnsProperties.previousPercentages.right + percentageDiff <
            0) {
            currentPercentages.left =
                columnsProperties.previousPercentages.left +
                    Math.abs(columnsProperties.previousPercentages.right);
            currentPercentages.right = 0;
        }
        else {
            currentPercentages.left =
                columnsProperties.previousPercentages.left - percentageDiff;
            currentPercentages.right =
                columnsProperties.previousPercentages.right + percentageDiff;
        }
        const tempColumns = columns;
        tempColumns[draggedHandleIndex].value = currentPercentages.left;
        tempColumns[rightDraggedIndex].value = currentPercentages.right;
        onChange({ newColumns: [...tempColumns], origin: 'drag-handle' });
    };
    const handleMouseDown = ({ event, leftIndex, rightIndex, }) => {
        columnsProperties.previousX = event.clientX;
        columnsProperties.previousPercentages.left = columns[leftIndex].value;
        columnsProperties.previousPercentages.right =
            columns[rightIndex].value;
        setIsMouseDraggingHandle(true);
        setDraggedHandleIndex(leftIndex);
    };
    const handleKeyDownHandler = ({ event, leftIndex, }) => {
        const rightIndex = leftIndex + 1;
        const origin = 'drag-handle';
        const newColumns = [...columns];
        const rightArrow = 'ArrowRight';
        const leftArrow = 'ArrowLeft';
        if (event.key === rightArrow) {
            if (newColumns[leftIndex].value >= minValue &&
                newColumns[rightIndex].value > minValue) {
                newColumns[leftIndex].value++;
                newColumns[rightIndex].value--;
                setKeyboardDraggingHandle(leftIndex);
                onChange({ newColumns, origin });
            }
        }
        if (event.key === leftArrow) {
            if (newColumns[leftIndex].value > minValue &&
                newColumns[rightIndex].value >= minValue) {
                newColumns[rightIndex].value++;
                newColumns[leftIndex].value--;
                setKeyboardDraggingHandle(leftIndex);
                onChange({ newColumns, origin });
            }
        }
    };
    const DragHandle = ({ leftIndex }) => {
        const rightIndex = leftIndex + 1;
        return (React.createElement("div", { "data-hook": `column_handle_${leftIndex}`, onMouseDown: (event) => handleMouseDown({ event, leftIndex, rightIndex }), onMouseMove: (event) => !disabled && handleMouseMove({ event, leftIndex }), onMouseUp: () => {
                setIsMouseDraggingHandle(false);
                setDraggedHandleIndex(-1);
            }, onKeyDown: (event) => handleKeyDownHandler({ event, leftIndex }), tabIndex: tabIndex, onBlur: () => setKeyboardDraggingHandle(-1), className: st(classes.dragHandle, {
                disabled,
                isDragging: blockDragged !== -1,
                isActive: draggedHandleIndex === leftIndex,
            }), ref: (el) => (handleRef.current[leftIndex] = el) }, context.theme === 'responsive' ? (React.createElement(ResponsiveHandle, { color: stVars['color-b1'] })) : (React.createElement(ClassicHandle, { color: stVars['color-b1'] }))));
    };
    const thumbsSpace = columns.length - 1;
    const onDragStartHandler = (e) => {
        onDragStart && onDragStart(e);
    };
    const onDragUpdateHandler = (e) => {
        onDragUpdate && onDragUpdate(e);
    };
    const onDragEndHandler = (result) => {
        const { destination, source } = result;
        setBlockDragged(-1);
        onDragEnd && onDragEnd(result);
        if (!destination) {
            // drops out side the <Droppable/> area
            return;
        }
        if (destination.droppableId === source.droppableId &&
            destination.index === source.index) {
            // drops to the same spot
            return;
        }
        const newColumns = [...columns];
        newColumns.splice(source.index, 1);
        newColumns.splice(destination.index, 0, columns[source.index]);
        const origin = 'drag-column';
        onChange({ newColumns, origin });
    };
    const draggedItem = (provided, _snapshot, rubric) => (React.createElement("div", { ...provided.draggableProps, ...provided.dragHandleProps, ref: provided.innerRef },
        React.createElement("div", { className: st(classes.draggedItem, {
                isDragging: isMouseDraggingHandle,
            }) },
            React.createElement(Text, { size: "tiny", skin: "standard-light" }, rubric.source.index + 1)),
        React.createElement("div", { className: classes.draggedItemInputWrapper },
            React.createElement(Input, { className: st(classes.inputClass, context.appFlagsClass), value: `${columns[rubric.source.index].value.toFixed(0)}%`, size: "small", 
                // required prop but not used here - only ui
                onChange: () => { } }))));
    const renderActions = () => {
        return allowActions ? (React.createElement("div", { className: classes.actionsContainer },
            addButtonNode,
            React.cloneElement(removeButtonNode, {
                onClick: () => removeButtonNode.props.onClick(selectedBlock),
            }),
            React.cloneElement(duplicateButtonNode, {
                onClick: () => duplicateButtonNode.props.onClick(selectedBlock),
            }))) : null;
    };
    const renderPlaceholder = (columnValue) => {
        return (React.createElement("div", { className: classes.placeholder, style: {
                width: `${columnValue}%`,
            } }));
    };
    const renderColumn = (column, index) => {
        const columnWidth = ((barRef.current?.offsetWidth - thumbsSpace * thumbsHeight) *
            column.value) /
            100;
        const renderBlock = () => {
            const pairColumnIndex = index === columns.length - 1 ? index - 1 : index + 1;
            return (React.createElement(Draggable, { draggableId: column.id, index: index, isDragDisabled: !allowReorder || disabled }, (provided) => (React.createElement("div", { className: classes.blockWrapper, ref: provided.innerRef, ...provided.draggableProps, ...provided.dragHandleProps, style: {
                    width: `${column.value}%`,
                    ...provided.draggableProps.style,
                }, tabIndex: -1, "data-hook": `column_block_${index}` },
                React.createElement("div", { onClick: () => {
                        if (allowActions &&
                            !disabled &&
                            selectedBlock !== index) {
                            setSelectedBlock(index);
                            return;
                        }
                    }, onMouseMove: (event) => !disabled &&
                        handleMouseMove({
                            event,
                            leftIndex: index,
                        }), onMouseUp: () => {
                        setIsMouseDraggingHandle(false);
                        setDraggedHandleIndex(-1);
                    }, className: st(classes.block, {
                        disabled,
                        allowReorder,
                        allowActions: allowActions && !disabled,
                        selected: selectedBlock === index && allowActions,
                    }), id: column.id, tabIndex: tabIndex },
                    React.createElement(Text, { className: st(classes.columnNumber, {
                            // keeping JS based solution, since CSS @container isn't widely supported yet
                            // TODO: remove when CSS @container falls under wix supported browser range
                            hidden: columnWidth > 0 ? columnWidth < 12 : column.value < 6,
                        }), size: "tiny", skin: selectedBlock === index && allowActions
                            ? 'standard-light'
                            : 'link', disabled: disabled }, index + 1)),
                React.createElement(ColumnControllerInput, { dataHook: `column_input_${index}`, className: classes.inputClass, disabled: disabled, onChange: ({ currentColumnValue, pairColumnValue }) => {
                        const origin = 'manual-input';
                        const newColumns = [...columns];
                        newColumns[index].value = currentColumnValue;
                        newColumns[pairColumnIndex].value = pairColumnValue;
                        onChange({ newColumns, origin });
                    }, pairColumnValue: columns[pairColumnIndex].value, currentColumnValue: column.value, minValue: minValue })))));
        };
        const renderDragHandle = () => {
            return (React.createElement("div", { className: st(classes.handleContainer) },
                React.createElement(DragHandle, { leftIndex: index })));
        };
        const shouldRenderPlaceholder = blockDragged === index;
        const shouldRenderDragHandle = index < columns.length - 1;
        return (React.createElement(React.Fragment, { key: index + column.id },
            renderBlock(),
            shouldRenderPlaceholder && renderPlaceholder(column.value),
            shouldRenderDragHandle && renderDragHandle()));
    };
    const renderDroppableArea = () => {
        return (React.createElement(Droppable, { droppableId: "all-columns", direction: "horizontal", type: "column", mode: "virtual", renderClone: draggedItem, isDropDisabled: !allowReorder || disabled }, (provided) => {
            return (React.createElement("div", { "data-hook": dataHook, className: st(classes.sliderContainer, { disabled }), ...provided.droppableProps, ref: mergeRefs([barRef, provided.innerRef]), tabIndex: -1 }, columns.map(renderColumn)));
        }));
    };
    return (React.createElement(DragDropContext, { ref: ref, onBeforeDragStart: (e) => {
            setBlockDragged(e.source.index);
        }, onDragStart: onDragStartHandler, onDragEnd: onDragEndHandler, onDragUpdate: onDragUpdateHandler },
        React.createElement("div", { className: classes.root },
            renderActions(),
            renderDroppableArea())));
});
export default ColumnController;
//# sourceMappingURL=ColumnController.js.map